import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Plus, Pencil, Trash2, Search, MapPin, Settings } from 'lucide-react';
import { SmartButton, DeleteButton } from '@/components/ui/interactive/SmartButton';
import { useToast } from '@/hooks/use-toast';
interface ServiceCountryMapping {
  id: string;
  service_name: string;
  country_name: string;
  is_available: boolean;
  price: number;
  currency: string;
  created_at: string;
}
export default function ServiceCountryMapping() {
  const [mappings, setMappings] = useState<ServiceCountryMapping[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCountry, setFilterCountry] = useState("all");
  const [filterService, setFilterService] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    service_name: '',
    country_name: '',
    is_available: true,
    price: 0,
    currency: 'SAR'
  });
  const {
    toast
  } = useToast();

  // Mock data for demonstration
  useEffect(() => {
    const mockMappings: ServiceCountryMapping[] = [{
      id: '1',
      service_name: 'حجز فندق',
      country_name: 'السعودية',
      is_available: true,
      price: 500,
      currency: 'SAR',
      created_at: new Date().toISOString()
    }, {
      id: '2',
      service_name: 'تأشيرة سياحية',
      country_name: 'تركيا',
      is_available: true,
      price: 200,
      currency: 'USD',
      created_at: new Date().toISOString()
    }, {
      id: '3',
      service_name: 'نقل المطار',
      country_name: 'الإمارات',
      is_available: false,
      price: 0,
      currency: 'AED',
      created_at: new Date().toISOString()
    }];
    setMappings(mockMappings);
  }, []);
  const filteredMappings = mappings.filter(mapping => {
    const matchesSearch = mapping.service_name.toLowerCase().includes(searchTerm.toLowerCase()) || mapping.country_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCountry = filterCountry === "all" || mapping.country_name === filterCountry;
    const matchesService = filterService === "all" || mapping.service_name === filterService;
    return matchesSearch && matchesCountry && matchesService;
  });
  return <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-green-600 rounded-lg flex items-center justify-center">
            <MapPin className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">ربط الخدمة بالدولة</h1>
            
          </div>
        </div>
        <Button className="gap-2" onClick={() => setIsDialogOpen(true)}>
          <Plus className="h-4 w-4" />
          إضافة ربط جديد
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>البحث والتصفية</CardTitle>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input placeholder="البحث في الخدمات والدول..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pr-10" />
            </div>
            <Select value={filterCountry} onValueChange={setFilterCountry}>
              <SelectTrigger>
                <SelectValue placeholder="تصفية حسب الدولة" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الدول</SelectItem>
                <SelectItem value="السعودية">السعودية</SelectItem>
                <SelectItem value="تركيا">تركيا</SelectItem>
                <SelectItem value="الإمارات">الإمارات</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterService} onValueChange={setFilterService}>
              <SelectTrigger>
                <SelectValue placeholder="تصفية حسب الخدمة" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الخدمات</SelectItem>
                <SelectItem value="حجز فندق">حجز فندق</SelectItem>
                <SelectItem value="تأشيرة سياحية">تأشيرة سياحية</SelectItem>
                <SelectItem value="نقل المطار">نقل المطار</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>قائمة الخدمات المربوطة</CardTitle>
          <CardDescription>
            عدد الخدمات المربوطة: {filteredMappings.length}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-right">اسم الخدمة</TableHead>
                <TableHead className="text-right">الدولة</TableHead>
                <TableHead className="text-right">الحالة</TableHead>
                <TableHead className="text-right">السعر</TableHead>
                <TableHead className="text-right">العملة</TableHead>
                <TableHead className="text-right">الاجراءات</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMappings.map(mapping => <TableRow key={mapping.id}>
                  <TableCell className="font-medium">{mapping.service_name}</TableCell>
                  <TableCell>{mapping.country_name}</TableCell>
                  <TableCell>
                    <Badge variant={mapping.is_available ? "default" : "secondary"}>
                      {mapping.is_available ? "متاحة" : "غير متاحة"}
                    </Badge>
                  </TableCell>
                  <TableCell>{mapping.price}</TableCell>
                  <TableCell>{mapping.currency}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <SmartButton 
                        variant="outline" 
                        size="sm"
                        icon={Pencil}
                        onClick={() => {
                          // Set form data for editing
                          setFormData({
                            service_name: mapping.service_name,
                            country_name: mapping.country_name,
                            is_available: mapping.is_available,
                            price: mapping.price,
                            currency: mapping.currency
                          });
                          setIsDialogOpen(true);
                        }}
                        className="text-blue-600 border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                      >
                        تعديل
                      </SmartButton>
                      <DeleteButton
                        itemName={`ربط ${mapping.service_name} مع ${mapping.country_name}`}
                        onClick={() => {
                          setMappings(prev => prev.filter(m => m.id !== mapping.id));
                          toast({
                            title: "تم الحذف",
                            description: "تم حذف الربط بنجاح"
                          });
                        }}
                        size="sm"
                      >
                        حذف
                      </DeleteButton>
                    </div>
                  </TableCell>
                </TableRow>)}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog for adding/editing mappings */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[425px]" dir="rtl">
          <DialogHeader>
            <DialogTitle>إضافة ربط جديد</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="service_name">اسم الخدمة</Label>
              <Input id="service_name" value={formData.service_name} onChange={e => setFormData({
              ...formData,
              service_name: e.target.value
            })} placeholder="أدخل اسم الخدمة" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="country_name">الدولة</Label>
              <Select value={formData.country_name} onValueChange={value => setFormData({
              ...formData,
              country_name: value
            })}>
                <SelectTrigger>
                  <SelectValue placeholder="اختر الدولة" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="السعودية">السعودية</SelectItem>
                  <SelectItem value="تركيا">تركيا</SelectItem>
                  <SelectItem value="الإمارات">الإمارات</SelectItem>
                  <SelectItem value="مصر">مصر</SelectItem>
                  <SelectItem value="الأردن">الأردن</SelectItem>
                  <SelectItem value="لبنان">لبنان</SelectItem>
                  <SelectItem value="الكويت">الكويت</SelectItem>
                  <SelectItem value="قطر">قطر</SelectItem>
                  <SelectItem value="البحرين">البحرين</SelectItem>
                  <SelectItem value="عمان">عمان</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="price">السعر</Label>
              <Input id="price" type="number" value={formData.price} onChange={e => setFormData({
              ...formData,
              price: parseFloat(e.target.value) || 0
            })} placeholder="أدخل السعر" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency">العملة</Label>
              <Select value={formData.currency} onValueChange={value => setFormData({
              ...formData,
              currency: value
            })}>
                <SelectTrigger>
                  <SelectValue placeholder="اختر العملة" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SAR">ريال سعودي (SAR)</SelectItem>
                  <SelectItem value="USD">دولار أمريكي (USD)</SelectItem>
                  <SelectItem value="EUR">يورو (EUR)</SelectItem>
                  <SelectItem value="AED">درهم إماراتي (AED)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>إلغاء</Button>
            <Button onClick={() => {
            // Handle form submission
            console.log('Adding mapping:', formData);
            setIsDialogOpen(false);
            toast({
              title: "تم إضافة الربط",
              description: "تم إضافة ربط الخدمة بالدولة بنجاح"
            });
          }}>
              إضافة
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>;
}